import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import OrganizationFeature from "@mapmycustomers/shared/enum/OrganizationFeature";
import {
  EntityType,
  EntityTypesSupportingRoutes,
  Group,
} from "@mapmycustomers/shared/types/entity";
import Route from "@mapmycustomers/shared/types/entity/Route";
import { Button, LoadingSpinner, Modal } from "@mapmycustomers/ui";

import backgroundShape from "@app/assets/map/leadFinder/background.svg";
import checkmark from "@app/assets/map/leadFinder/checkmark.svg";
import companiesIcon from "@app/assets/map/leadFinder/companies.svg";
import peopleIcon from "@app/assets/map/leadFinder/people.svg";
import ButtonLink from "@app/component/ButtonLink";
import { finishAddRecords } from "@app/component/leadFinder/store/actions";
import { LeadFinderProgressState } from "@app/component/leadFinder/store/LeadFinderState";
import {
  getAddRecordsAvailableRoutes,
  getAddRecordsEntityType,
  getAddRecordsProgress,
  isAddRecordsResultLoading,
  isResultModalVisible,
} from "@app/component/leadFinder/store/selectors";
import Path from "@app/enum/Path";
import RouteSubPath from "@app/enum/RouteSubPath";
import { getGroupsForEntity } from "@app/store/groups";
import { RootState } from "@app/store/rootReducer";
import useFeatures from "@app/util/hook/useFeatures";
import { getColorDisplayName } from "@app/util/ui";

import Count from "./components/Count";
import messages from "./messages";
import styles from "./ResultsModal.module.scss";

interface Props {
  entityType: EntityTypesSupportingRoutes;
  finishAddRecords: typeof finishAddRecords;
  groups: Group[];
  loading: boolean;
  progress: LeadFinderProgressState;
  routes: Route[];
  visible: boolean;
}

const ResultsModal = ({
  entityType,
  finishAddRecords,
  groups,
  loading,
  progress,
  routes,
  visible,
}: Props) => {
  const intl = useIntl();

  const handleClose = useCallback(() => {
    finishAddRecords();
  }, [finishAddRecords]);

  const filteredGroups = useMemo(() => {
    const { groupIds } = progress;
    if (groupIds?.length) {
      return groups
        .filter((group) => groupIds.includes(group.id))
        .map<React.ReactNode>((group) => (
          <Tooltip key={group.id} placement="bottom" title={intl.formatMessage(messages.openGroup)}>
            <ButtonLink href={`${Path.MAP}/groups/${entityType}/${group.id}`} target="_blank">
              {group.name}
            </ButtonLink>
          </Tooltip>
        ))
        .reduce<React.ReactNode[]>(
          (result, curr) => [...result, ...(result.length ? ", " : ""), curr],
          []
        );
    }

    return undefined;
  }, [entityType, groups, intl, progress]);
  const filteredRoutes = useMemo(() => {
    const { newRouteIds, routeIds } = progress;
    if (routeIds?.length) {
      return routes
        .filter((route) => routeIds.includes(route.id))
        .map<React.ReactNode>((route) => (
          <Tooltip key={route.id} placement="bottom" title={intl.formatMessage(messages.openRoute)}>
            <ButtonLink href={`${Path.ROUTE}/${RouteSubPath.VIEW}/${route.id}`} target="_blank">
              {newRouteIds?.includes(route.id)
                ? intl.formatMessage(messages.newRouteName, { name: route.name })
                : route.name}
            </ButtonLink>
          </Tooltip>
        ))
        .reduce<React.ReactNode[]>(
          (result, curr) => [...result, ...(result.length ? ", " : ""), curr],
          []
        );
    }

    return undefined;
  }, [routes, intl, progress]);

  const filteredRouteErrors = useMemo(() => {
    const { routeErrors } = progress;
    if (routeErrors && Object.keys(routeErrors).length) {
      return routes
        .filter((route) => routeErrors[route.id]?.length)
        .map<React.ReactNode>((route) => (
          <>
            {routeErrors[route.id].map((error, index) => (
              <div key={`${route.id}-${index}`}>
                {intl.formatMessage(messages.errorAddingRoute, {
                  error,
                  name: route.name,
                })}
              </div>
            ))}
          </>
        ));
    }

    return undefined;
  }, [intl, progress, routes]);

  const { hasFeature } = useFeatures();

  return visible ? (
    <Modal
      centered
      className={styles.container}
      closable={false}
      destroyOnClose
      footer={null}
      maskClosable
      maskStyle={{
        backgroundColor: "transparent",
      }}
      onCancel={handleClose}
      open
      width={630}
    >
      <div className={styles.background}>
        <img alt="background shape" src={backgroundShape} />
      </div>

      <div className={styles.icon}>
        <img alt="icon" src={entityType === EntityType.COMPANY ? companiesIcon : peopleIcon} />
      </div>

      {progress.entityIds && <Count count={progress.entityIds.length} entityType={entityType} />}

      <div className={styles.content}>
        <div className={styles.stageList}>
          {!hasFeature(OrganizationFeature.DISALLOW_COLOR) && progress.color && (
            <div className={styles.stage}>
              <div className={styles.stageIcon}>
                <img alt="checkmark" src={checkmark} />
              </div>

              <div className={styles.stageDetails}>
                <h5>{intl.formatMessage(messages.actionColor)}</h5>
                <div className={styles.stageInfo}>{getColorDisplayName(intl, progress.color)}</div>
              </div>
            </div>
          )}

          {progress.groupIds?.length && (
            <div className={styles.stage}>
              <div className={styles.stageIcon}>
                <img alt="checkmark" src={checkmark} />
              </div>

              <div className={styles.stageDetails}>
                <h5>
                  {intl.formatMessage(messages.actionGroup, { count: progress.groupIds.length })}
                </h5>
                <div className={styles.stageInfo}>{filteredGroups}</div>
              </div>
            </div>
          )}

          {(progress.routeIds?.length || progress.routeErrors) && (
            <div className={styles.stage}>
              <div className={styles.stageIcon}>
                <img alt="checkmark" src={checkmark} />
              </div>

              <div className={styles.stageDetails}>
                {progress.routeIds && (
                  <>
                    <h5>
                      {intl.formatMessage(messages.actionRoutes, {
                        count: progress.routeIds.length,
                      })}
                    </h5>

                    <div className={styles.stageInfo}>{filteredRoutes}</div>
                  </>
                )}

                {progress.routeErrors && (
                  <div className={styles.stageErrors}>{filteredRouteErrors}</div>
                )}
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className={styles.btnContainer}>
            <Button className={styles.btn} onClick={handleClose}>
              {intl.formatMessage(messages.returnButton)}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  ) : null;
};

const mapStateToProps = (state: RootState) => {
  const entityType = getAddRecordsEntityType(state);
  return {
    entityType,
    groups: getGroupsForEntity(state, entityType),
    loading: isAddRecordsResultLoading(state),
    progress: getAddRecordsProgress(state),
    routes: getAddRecordsAvailableRoutes(state),
    visible: isResultModalVisible(state),
  };
};

const mapDispatchToProps = {
  finishAddRecords,
};

export default connect(mapStateToProps, mapDispatchToProps)(ResultsModal);
